import React from 'react';
import { HeroSection } from './HeroSection';
import { BonusSection } from './BonusSection';
import { PurchaseSection } from './PurchaseSection';
import { SocialProofPopup } from './SocialProofPopup';
import { CTAButton } from './CTAButton';
import { FAQSection } from './FAQSection';
import { CommentsSection } from './CommentsSection';
import { AnimatedImage } from './AnimatedImage';
import { StickyBar } from './StickyBar';
import { HeartIcon, BrainIcon, ShieldCheckIcon, AlertTriangleIcon, HelpCircleIcon, CheckCircleIcon, SparklesIcon, DogIcon } from 'lucide-react';
export function SalesPage() {
  const scrollToPurchase = () => {
    document.getElementById('purchase-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <main className="min-h-screen bg-stone-50 font-body selection:bg-emerald-200" data-id="element-107">
      <StickyBar data-id="element-108" />
      <HeroSection data-id="element-109" />

      <article className="bg-white" data-id="element-110">
        {/* ── BLOCK 1: INTRO ── */}
        <section className="max-w-3xl mx-auto px-5 py-14 md:py-20 text-center space-y-6 pt-[14px] pb-[14px] relative overflow-hidden" data-id="element-111">
          {/* Animated paw prints */}
          {[{
          top: '8%',
          left: '5%',
          size: 22,
          rotate: -20,
          opacity: 0.15,
          delay: 0
        }, {
          top: '60%',
          left: '2%',
          size: 18,
          rotate: 15,
          opacity: 0.12,
          delay: 1.2
        }, {
          top: '25%',
          right: '4%',
          size: 26,
          rotate: 30,
          opacity: 0.18,
          delay: 0.6
        }, {
          top: '72%',
          right: '8%',
          size: 20,
          rotate: -10,
          opacity: 0.14,
          delay: 2.0
        }, {
          top: '45%',
          left: '8%',
          size: 16,
          rotate: 40,
          opacity: 0.1,
          delay: 1.8
        }, {
          top: '15%',
          right: '12%',
          size: 14,
          rotate: -35,
          opacity: 0.12,
          delay: 0.9
        }, {
          top: '85%',
          left: '15%',
          size: 20,
          rotate: 25,
          opacity: 0.13,
          delay: 2.5
        }, {
          top: '50%',
          right: '2%',
          size: 15,
          rotate: -5,
          opacity: 0.11,
          delay: 3.0
        }].map((paw, i) => <svg key={i} className="paw-print" style={{
          top: paw.top,
          left: paw.left,
          right: paw.right,
          width: paw.size,
          height: paw.size,
          '--paw-rotate': `${paw.rotate}deg`,
          '--paw-opacity': paw.opacity,
          animationDelay: `${paw.delay}s`
        } as React.CSSProperties} viewBox="0 0 512 512" fill="currentColor" data-id="element-112">
              <path d="M226.5 92.9c14.3 42.9-.3 86.2-32.6 96.8s-70.1-15.6-84.4-58.5 .3-86.2 32.6-96.8 70.1 15.6 84.4 58.5zM100.4 198.6c18.9 32.4 14.3 70.1-10.2 84.1s-59.7-.9-78.5-33.3S-2.7 179.3 21.8 165.3s59.7 .9 78.5 33.3zM69.2 401.2C121.6 259.9 214.7 224 256 224s134.4 35.9 186.8 177.2c3.6 9.7 5.2 20.1 5.2 30.5v1.6c0 25.8-20.9 46.7-46.7 46.7-11.5 0-22.9-1.4-34-4.2l-88-22c-15.3-3.8-31.3-3.8-46.6 0l-88 22c-11.1 2.8-22.5 4.2-34 4.2C84.9 480 64 459.1 64 433.3v-1.6c0-10.4 1.6-20.8 5.2-30.5zM421.8 282.7c-24.5-14-29.1-51.7-10.2-84.1s54-47.3 78.5-33.3 29.1 51.7 10.2 84.1-54 47.3-78.5 33.3zM310.1 189.7c-32.3-10.6-46.9-53.9-32.6-96.8s52.1-69.1 84.4-58.5 46.9 53.9 32.6 96.8-52.1 69.1-84.4 58.5z" data-id="element-113" />
            </svg>)}

          <div className="flex justify-center relative z-10" data-id="element-114">
            <div className="w-14 h-14 rounded-full bg-pink-100 flex items-center justify-center" data-id="element-115">
              <HeartIcon className="w-8 h-8 text-pink-500" data-id="element-116" />
            </div>
          </div>
          <p className="text-xl md:text-2xl text-stone-700 leading-relaxed pt-[0px] pb-[0px] relative z-10" data-id="element-117">
            Vos lo amás. Lo cuidás. Es parte de tu familia.
          </p>
          <div className="border-l-4 border-[#79000C] bg-red-50 rounded-r-xl px-6 py-5 mx-auto max-w-xl relative z-10" data-id="element-118">
            <p className="text-xl md:text-2xl font-[900] text-[#79000C] text-left leading-snug" data-id="element-119">
              Pero si le estás dando croquetas estás deteriorando su vida.
            </p>
          </div>
        </section>

        {/* ── Divider ── */}
        <div className="max-w-xs mx-auto border-t border-stone-200" data-id="element-120" />

        {/* ── BLOCK 2: PENSALO UN SEGUNDO ── */}
        <section className="max-w-3xl mx-auto px-5 py-14 md:py-20 text-center space-y-6 pt-[13px] pb-[13px]" data-id="element-121">
          <div className="flex justify-center" data-id="element-122">
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center" data-id="element-123">
              <BrainIcon className="w-8 h-8 text-amber-600" data-id="element-124" />
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900" data-id="element-125">
            Pensalo un segundo
          </h2>
          <div className="inline-block bg-[#D37C7C] rounded-2xl px-6 py-4 md:px-10 md:py-5" data-id="element-126">
            <p className="font-semibold text-white text-lg md:text-xl leading-relaxed" data-id="element-127">
              ¿Vos comerías lo mismo… ultraprocesado… todos los días de tu vida?
            </p>
          </div>
        </section>

        {/* ── Divider ── */}
        <div className="max-w-xs mx-auto border-t border-stone-200" data-id="element-128" />

        {/* ── BLOCK 3: TU PERRO CONFÍA EN VOS ── */}
        <section className="max-w-3xl mx-auto px-5 py-14 md:py-20 text-center space-y-6 pt-[0px] pb-[0px]" data-id="element-129">
          <div className="flex justify-center" data-id="element-130">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center" data-id="element-131">
              <ShieldCheckIcon className="w-8 h-8 text-emerald-600" data-id="element-132" />
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-stone-900" data-id="element-133">
            Tu perro confía en vos
          </h2>
          <p className="text-lg md:text-xl text-stone-700" data-id="element-134">
            Confía en que lo vas a alimentar bien.
          </p>
          <div className="space-y-2 pt-4" data-id="element-135">
            <p className="text-lg md:text-xl font-bold text-stone-900" data-id="element-136">
              Las croquetas no son lo que parecen.
            </p>
            <p className="text-lg md:text-xl text-stone-600" data-id="element-137">Y eso se nota.</p>
          </div>
        </section>

        {/* ── BLOCK 4: SYMPTOMS GRID ── */}
        <section className="max-w-2xl mx-auto px-5 py-10" data-id="element-138">
          <div className="bg-[#FF7F7F] rounded-2xl p-6 md:p-8" data-id="element-139">
            <div className="grid grid-cols-2 gap-4" data-id="element-140">
              <div className="bg-white/30 backdrop-blur-sm rounded-xl p-4 flex flex-col items-center text-center gap-2" data-id="element-141">
                <AlertTriangleIcon className="w-7 h-7 text-red-800" data-id="element-142" />
                <span className="font-[900] text-[#000000] text-base md:text-lg" data-id="element-143">
                  Alergias
                </span>
              </div>
              <div className="bg-white/30 backdrop-blur-sm rounded-xl p-4 flex flex-col items-center text-center gap-2" data-id="element-144">
                <AlertTriangleIcon className="w-7 h-7 text-red-800" data-id="element-145" />
                <span className="font-[900] text-[#000000] text-base md:text-lg" data-id="element-146">
                  Problemas digestivos
                </span>
              </div>
              <div className="bg-white/30 backdrop-blur-sm rounded-xl p-4 flex flex-col items-center text-center gap-2" data-id="element-147">
                <AlertTriangleIcon className="w-7 h-7 text-red-800" data-id="element-148" />
                <span className="font-[900] text-[#000000] text-base md:text-lg" data-id="element-149">
                  Picores
                </span>
              </div>
              <div className="bg-white/30 backdrop-blur-sm rounded-xl p-4 flex flex-col items-center text-center gap-2" data-id="element-150">
                <AlertTriangleIcon className="w-7 h-7 text-red-800" data-id="element-151" />
                <span className="font-[900] text-[#000000] text-base md:text-lg" data-id="element-152">
                  Falta de energía
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* ── Divider ── */}
        <div className="max-w-xs mx-auto border-t border-stone-200" data-id="element-153" />

        {/* ── BLOCK 5: LA PREGUNTA ES SIMPLE ── */}
        <section className="max-w-3xl mx-auto px-5 py-14 md:py-20 text-center space-y-8 pt-[16px] pb-[16px]" data-id="element-154">
          <div className="flex justify-center" data-id="element-155">
            <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center" data-id="element-156">
              <HelpCircleIcon className="w-8 h-8 text-[#62104C]" data-id="element-157" />
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl text-stone-900 font-[100]" data-id="element-158">
            La pregunta es simple
          </h2>
          <p className="font-bold text-2xl md:text-3xl text-[#62104C] leading-snug max-w-lg mx-auto" data-id="element-159">
            ¿Vas a seguir haciendo lo mismo…
            <br data-id="element-160" />o vas a empezar a hacerlo mejor?
          </p>
        </section>

        {/* ── BLOCK 7: BOOK IMAGE + TITLE ── */}
        <section className="max-w-3xl mx-auto px-5 py-14 md:py-20 text-center space-y-10" data-id="element-161">
          <div className="flex justify-center" data-id="element-162">
            <AnimatedImage src="https://cdn.magicpatterns.com/uploads/dF1mGdVqZkT9wMFT1U1usC/Libro_Principal.png" alt="Guía Croquetas o Comida Real" className="w-full max-w-sm drop-shadow-2xl cursor-pointer hover:scale-105" onClick={scrollToPurchase} data-id="element-163" />
          </div>

          <div className="space-y-4" data-id="element-164">
            <h2 className="text-4xl md:text-5xl font-black text-stone-900" data-id="element-165">
              CROQUETAS O COMIDA REAL
            </h2>
            <p className="text-xl font-medium text-stone-600 max-w-lg mx-auto" data-id="element-166">
              Una guía clara para aprender a alimentar a tu perro de forma
              natural, simple y segura.
            </p>
          </div>
        </section>

        {/* ── BLOCK 8: FEATURES LIST ── */}
        <section className="max-w-2xl mx-auto px-5 py-10 pt-[13px] pb-[13px] space-y-[25px]" data-id="element-167">
          <div className="bg-[#B5FFC9] p-8 md:p-10 rounded-2xl shadow-lg border border-emerald-100" data-id="element-168">
            <p className="font-bold text-2xl mb-8 text-stone-900 text-center" data-id="element-169">
              Vas a encontrar:
            </p>
            <ul className="space-y-5" data-id="element-170">
              <li className="flex items-start bg-white/50 rounded-xl px-5 py-4 gap-4" data-id="element-171">
                <CheckCircleIcon className="w-6 h-6 text-[#21B910] flex-shrink-0 mt-0.5" data-id="element-172" />
                <span className="text-stone-800 text-lg text-left" data-id="element-173">
                  Explicación detallada sobre alimentación natural (BARF)
                </span>
              </li>
              <li className="flex items-start bg-white/50 rounded-xl px-5 py-4 gap-4" data-id="element-174">
                <CheckCircleIcon className="w-6 h-6 text-[#21B910] flex-shrink-0 mt-0.5" data-id="element-175" />
                <span className="text-stone-800 text-lg text-left" data-id="element-176">
                  Introducción a la alimentación Natural y cuáles son sus
                  beneficios
                </span>
              </li>
              <li className="flex items-start bg-white/50 rounded-xl px-5 py-4 gap-4" data-id="element-177">
                <CheckCircleIcon className="w-6 h-6 text-[#21B910] flex-shrink-0 mt-0.5" data-id="element-178" />
                <span className="text-stone-800 text-lg text-left" data-id="element-179">
                  Dietas específicas
                </span>
              </li>
              <li className="flex items-start bg-white/50 rounded-xl px-5 py-4 gap-4" data-id="element-180">
                <CheckCircleIcon className="w-6 h-6 text-[#21B910] flex-shrink-0 mt-0.5" data-id="element-181" />
                <span className="text-stone-800 text-lg text-left" data-id="element-182">
                  Recetas para cada etapa de la vida de tu Perrito
                </span>
              </li>
            </ul>
          </div>
        </section>

        {/* ── BLOCK 9: TAGLINE ── */}
        <section className="max-w-3xl mx-auto px-5 py-8 text-center pt-[15px] pb-[15px]" data-id="element-183">
          <p className="font-bold text-xl text-emerald-700" data-id="element-184">
            Sin vueltas. Sin confusión. Sin complicaciones.
          </p>
        </section>

        {/* ── Divider ── */}
        <div className="max-w-xs mx-auto border-t border-stone-200" data-id="element-185" />

        {/* ── BLOCK 11: TU PERRO NO PUEDE ELEGIR ── */}
        <section className="max-w-3xl mx-auto px-5 py-14 md:py-20 text-center space-y-6 pt-[1px] pb-[1px]" data-id="element-186">
          <div className="flex justify-center" data-id="element-187">
            <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center" data-id="element-188">
              <DogIcon className="w-8 h-8 text-orange-600" data-id="element-189" />
            </div>
          </div>
          <h2 className="text-[28px] md:text-4xl font-bold text-stone-900" data-id="element-190">
            Tu perro no puede elegir
          </h2>
          <div className="inline-block bg-[#FFB3BB] rounded-full px-8 py-3" data-id="element-191">
            <p className="text-xl font-semibold text-stone-900" data-id="element-192">Pero vos sí.</p>
          </div>
        </section>

        {/* ── BLOCK 12: EMPEZÁ HOY FINAL CTA ── */}
        <section className="max-w-3xl mx-auto px-5 py-10 md:py-16" data-id="element-193">
          <div className="bg-[#FEE245] p-8 md:p-12 rounded-3xl text-center shadow-2xl space-y-6" data-id="element-194">
            <div className="flex justify-center" data-id="element-195">
              <div className="w-14 h-14 rounded-full bg-yellow-100 flex items-center justify-center" data-id="element-196">
                <SparklesIcon className="w-8 h-8 text-yellow-600" data-id="element-197" />
              </div>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-[#000000]" data-id="element-198">
              Empezá hoy
            </h2>
            <p className="text-xl text-[#000000] max-w-md mx-auto" data-id="element-199">
              Descubrí cómo mejorar su vida empezando por lo más importante:
              <span className="font-bold text-2xl mt-2 block text-[#000000]" data-id="element-200">
                lo que hay en su plato.
              </span>
            </p>
            <CTAButton text="OBTENER GUÍA AHORA" data-id="element-201" />
          </div>
        </section>
      </article>

      <BonusSection data-id="element-202" />
      <PurchaseSection data-id="element-203" />
      <CommentsSection data-id="element-204" />
      <FAQSection data-id="element-205" />
      <SocialProofPopup data-id="element-206" />
    </main>;
}